import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Table,
  Card,
  Button,
  Input,
  Tag,
  Typography,
  Space,
  Modal,
  Tabs,
  message,
  Tooltip,
  Avatar,
  Empty,
  Spin,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  UserAddOutlined,
  SearchOutlined,
  DeleteOutlined,
  MailOutlined,
  PhoneOutlined,
  LeftOutlined,
  RightOutlined,
  CloseCircleFilled,
  CopyOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { api } from '@/services/api';
import { API_ENDPOINTS, PAGE_SIZE } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { formatRelative } from '@/utils/date';
import type { ApiResponse, TeacherStudentConnection } from '@/types';

const { Title, Text, Paragraph } = Typography;




interface InviteResponse {
  connection: TeacherStudentConnection;
  inviteCode: string;
}

export default function StudentsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [mainTab, setMainTab] = useState<'active' | 'invited'>('active');
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteMethod, setInviteMethod] = useState<'email' | 'phone'>('email');
  const [inviteValue, setInviteValue] = useState('');
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);

  const { data: studentsResponse, isLoading } = useQuery({
    queryKey: ['teacher', 'students'],
    queryFn: () =>
      api.get<never, ApiResponse<TeacherStudentConnection[]>>(API_ENDPOINTS.TEACHER_STUDENTS),
  });

  const connections = studentsResponse?.data ?? [];
  const activeConnections = connections.filter((c) => c.status === 'ACTIVE');
  const invitedConnections = connections.filter((c) => c.status === 'INVITED');

  const filteredConnections = useMemo(() => {
    if (!searchTerm.trim()) return activeConnections;
    const lower = searchTerm.toLowerCase();
    return activeConnections.filter((conn) =>
      conn.student?.name?.toLowerCase().includes(lower),
    );
  }, [activeConnections, searchTerm]);

  const totalPages = Math.ceil(filteredConnections.length / PAGE_SIZE);
  const pagedConnections = filteredConnections.slice(
    currentPage * PAGE_SIZE,
    (currentPage + 1) * PAGE_SIZE,
  );

  const inviteMutation = useMutation({
    mutationFn: (payload: { emailOrPhone: string }) =>
      api.post<never, ApiResponse<InviteResponse>>(API_ENDPOINTS.INVITE_STUDENT, payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['teacher', 'students'] });
      const code = res.data?.inviteCode;
      if (code) {
        setGeneratedCode(code);
        message.success('Invitation sent successfully');
      } else {
        message.success('Student invited');
        closeInviteModal();
      }
    },
    onError: (err: { error?: { message?: string } }) => {
      message.error(err?.error?.message ?? 'Failed to send invitation. Check that the student is registered.');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (connectionId: string) =>
      api.delete<never, ApiResponse<void>>(`${API_ENDPOINTS.CONNECTIONS}/${connectionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher', 'students'] });
      message.success('Connection removed');
    },
    onError: () => {
      message.error('Failed to remove connection');
    },
  });

  const openInviteModal = () => {
    setInviteValue('');
    setGeneratedCode(null);
    setInviteMethod('email');
    setInviteModalOpen(true);
  };

  const closeInviteModal = () => {
    setInviteModalOpen(false);
    setInviteValue('');
    setGeneratedCode(null);
  };

  const alreadyConnected = useMemo(() => {
    const val = inviteValue.trim().toLowerCase();
    if (!val) return null;
    return connections.find((c) => {
      const email = c.student?.email?.toLowerCase() ?? '';
      const phone = c.student?.phone?.toLowerCase() ?? '';
      return email === val || phone === val;
    }) ?? null;
  }, [inviteValue, connections]);

  const handleInviteSubmit = () => {
    if (!inviteValue.trim()) {
      message.warning(`Please enter an ${inviteMethod}`);
      return;
    }
    if (alreadyConnected) return;
    inviteMutation.mutate({ emailOrPhone: inviteValue.trim() });
  };

  const handleRemoveConfirm = (connectionId: string, studentName: string) => {
    Modal.confirm({
      title: 'Remove Student Connection',
      content: `Are you sure you want to remove "${studentName}" from your students? They will no longer see your decks.`,
      okText: 'Remove',
      okButtonProps: { danger: true },
      onOk: () => removeMutation.mutate(connectionId),
    });
  };

  const columns: ColumnsType<TeacherStudentConnection> = [
    {
      title: 'Student Name',
      key: 'name',
      render: (_, record) => (
        <Text strong style={{ color: '#6366f1' }}>
          {record.student?.name ?? 'Unknown'}
        </Text>
      ),
      sorter: (a, b) =>
        (a.student?.name ?? '').localeCompare(b.student?.name ?? ''),
    },
    {
      title: 'Email',
      key: 'email',
      render: (_, record) => (
        <Text type="secondary">
          {record.student?.email ?? '-'}
        </Text>
      ),
    },
    {
      title: 'Grade',
      key: 'grade',
      render: (_, record) => (
        <Space size={[0, 4]} wrap>
          {(record.student?.gradeIds ?? []).map((g) => (
            <Tag key={g}>{g === 0 ? 'KG' : `Grade ${g}`}</Tag>
          ))}
          {(!record.student?.gradeIds || record.student.gradeIds.length === 0) && (
            <Text type="secondary">-</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const s = record.student?.status ?? 'UNKNOWN';
        const color = s === 'ACTIVE' ? 'green' : s === 'INACTIVE' ? 'default' : 'red';
        return <Tag color={color}>{s}</Tag>;
      },
      filters: [
        { text: 'Active', value: 'ACTIVE' },
        { text: 'Inactive', value: 'INACTIVE' },
        { text: 'Suspended', value: 'SUSPENDED' },
      ],
      onFilter: (value, record) => record.student?.status === value,
    },
    {
      title: 'Last Active',
      key: 'lastActive',
      render: (_, record) => (
        <Text type="secondary">{formatRelative(record.student?.lastActiveAt)}</Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          {record.status !== 'REMOVED' && (
            <Tooltip title="Remove Connection">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveConfirm(record.id, record.student?.name ?? 'this student');
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          My Students
        </Title>
        <Button type="primary" icon={<UserAddOutlined />} onClick={openInviteModal}>
          Invite Student
        </Button>
      </div>

      <Tabs
        activeKey={mainTab}
        onChange={(key) => { setMainTab(key as 'active' | 'invited'); setCurrentPage(0); setSearchTerm(''); }}
        items={[
          {
            key: 'active',
            label: <span><UserAddOutlined /> Active ({activeConnections.length})</span>,
            children: (
              <>
                <Input
                  placeholder="Search by student name..."
                  prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(0);
                  }}
                  suffix={
                    searchTerm ? (
                      <CloseCircleFilled
                        style={{ fontSize: 16, color: '#8c8c8c', cursor: 'pointer' }}
                        onClick={() => {
                          setSearchTerm('');
                          setCurrentPage(0);
                        }}
                      />
                    ) : null
                  }
                  style={{ maxWidth: isMobile ? '100%' : 360, marginBottom: 16 }}
                />

                {isLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
                    <Spin size="large" />
                  </div>
                ) : filteredConnections.length === 0 ? (
                  <Card style={{ borderRadius: 12, textAlign: 'center', padding: '40px 0' }}>
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        searchTerm.trim()
                          ? `No students found matching "${searchTerm}"`
                          : 'No students yet. Invite your first student!'
                      }
                    />
                  </Card>
                ) : isMobile ? (
                  /* ── Mobile card list ── */
                  <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {pagedConnections.map((record) => {
                      const name = record.student?.name ?? 'Unknown';
                      const email = record.student?.email ?? null;
                      const grades = record.student?.gradeIds ?? [];
                      const lastActive = formatRelative(record.student?.lastActiveAt);

                      return (
                        <Card
                          key={record.id}
                          style={{ borderRadius: 12, cursor: 'pointer' }}
                          styles={{ body: { padding: '14px 16px' } }}
                          onClick={() => navigate(`/teacher/students/${record.studentId}`)}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                            <Avatar
                              size={40}
                              style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', fontWeight: 600, flexShrink: 0 }}
                            >
                              {name.charAt(0).toUpperCase()}
                            </Avatar>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text
                                strong
                                style={{ fontSize: 15, display: 'block', color: '#6366f1' }}
                              >
                                {name}
                              </Text>
                              {email && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {email}
                                </Text>
                              )}
                            </div>
                            {(() => {
                              const s = record.student?.status ?? 'UNKNOWN';
                              const color = s === 'ACTIVE' ? 'green' : s === 'INACTIVE' ? 'default' : 'red';
                              return <Tag color={color} style={{ margin: 0, flexShrink: 0 }}>{s}</Tag>;
                            })()}
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              {grades.length > 0
                                ? grades.map((g) => (
                                    <Tag key={g} style={{ margin: 0, fontSize: 12 }}>
                                      {g === 0 ? 'KG' : `Grade ${g}`}
                                    </Tag>
                                  ))
                                : <Text type="secondary" style={{ fontSize: 12 }}>No grade</Text>
                              }
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                · {lastActive}
                              </Text>
                            </div>

                            <div style={{ display: 'flex', gap: 4 }}>
                              {record.status !== 'REMOVED' && (
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={(e) => { e.stopPropagation(); handleRemoveConfirm(record.id, name); }}
                                  style={{ minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                />
                              )}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                  </>
                ) : (
                  /* ── Desktop table ── */
                  <Card style={{ borderRadius: 12 }}>
                    <Table
                      columns={columns}
                      dataSource={pagedConnections}
                      rowKey="id"
                      loading={isLoading}
                      pagination={false}
                      locale={{ emptyText: 'No students yet. Invite your first student!' }}
                      onRow={(record) => ({
                        onClick: () => navigate(`/teacher/students/${record.studentId}`),
                        style: { cursor: 'pointer' },
                      })}
                    />
                  </Card>
                )}

                {filteredConnections.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
                    <Button
                      icon={<LeftOutlined />}
                      disabled={currentPage === 0}
                      onClick={() => setCurrentPage((p) => p - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      disabled={currentPage >= totalPages - 1}
                      onClick={() => setCurrentPage((p) => p + 1)}
                    >
                      Next <RightOutlined />
                    </Button>
                  </div>
                )}
              </>
            ),
          },
          {
            key: 'invited',
            label: <span><ClockCircleOutlined /> Invited ({invitedConnections.length})</span>,
            children: (
              <>
                {invitedConnections.length === 0 ? (
                  <Card style={{ borderRadius: 12, textAlign: 'center', padding: '40px 0' }}>
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No pending invitations"
                    />
                  </Card>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {invitedConnections.map((record) => {
                      const isOpenInvite = record.studentId === record.teacherId;
                      const name = isOpenInvite ? 'Open Invitation' : (record.student?.name ?? 'Unknown');
                      const email = isOpenInvite ? null : (record.student?.email ?? null);
                      const inviteCode = record.inviteCode ?? '';

                      return (
                        <Card
                          key={record.id}
                          style={{ borderRadius: 12, border: '1px solid #fde68a', background: '#fffbeb' }}
                          styles={{ body: { padding: '14px 16px' } }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <Avatar
                              size={40}
                              style={{
                                background: isOpenInvite ? '#f59e0b' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                fontWeight: 600,
                                flexShrink: 0,
                              }}
                            >
                              {isOpenInvite ? '?' : name.charAt(0).toUpperCase()}
                            </Avatar>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text strong style={{ fontSize: 15, display: 'block' }}>
                                {name}
                              </Text>
                              {email && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {email}
                                </Text>
                              )}
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                Invited {formatRelative(record.invitedAt)}
                              </Text>
                            </div>
                            {inviteCode && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text
                                  strong
                                  style={{ fontFamily: 'monospace', fontSize: 14, letterSpacing: '0.1em' }}
                                >
                                  {inviteCode}
                                </Text>
                                <Button
                                  size="small"
                                  icon={<CopyOutlined />}
                                  onClick={() => {
                                    navigator.clipboard.writeText(inviteCode);
                                    message.success('Code copied!');
                                  }}
                                >
                                  Copy
                                </Button>
                              </div>
                            )}
                            <Button
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleRemoveConfirm(record.id, name)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </>
            ),
          },
        ]}
      />

      <Modal
        title="Invite Student"
        open={inviteModalOpen}
        onCancel={closeInviteModal}
        footer={
          generatedCode
            ? [
                <Button key="close" type="primary" onClick={closeInviteModal}>
                  Done
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={closeInviteModal}>
                  Cancel
                </Button>,
                <Button
                  key="submit"
                  type="primary"
                  loading={inviteMutation.isPending}
                  onClick={handleInviteSubmit}
                  disabled={!!alreadyConnected}
                >
                  Send Invite
                </Button>,
              ]
        }
      >
        {generatedCode ? (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <Paragraph>Invitation sent! Share this invite code with the student:</Paragraph>
            <div
              style={{
                background: '#f8fafc',
                padding: '16px 24px',
                borderRadius: 12,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 16,
              }}
            >
              <Text
                strong
                style={{ fontSize: 24, letterSpacing: '0.15em', fontFamily: 'monospace' }}
              >
                {generatedCode}
              </Text>
              <Button
                icon={<CopyOutlined />}
                onClick={() => {
                  navigator.clipboard.writeText(generatedCode);
                  message.success('Code copied!');
                }}
              >
                Copy
              </Button>
            </div>
            <Paragraph type="secondary" style={{ fontSize: 13 }}>
              The student can use this code to join your class in the CardWise app.
            </Paragraph>
          </div>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Tabs
              activeKey={inviteMethod}
              onChange={(key) => {
                setInviteMethod(key as 'email' | 'phone');
                setInviteValue('');
              }}
              items={[
                {
                  key: 'email',
                  label: (
                    <span>
                      <MailOutlined /> Email
                    </span>
                  ),
                },
                {
                  key: 'phone',
                  label: (
                    <span>
                      <PhoneOutlined /> Phone
                    </span>
                  ),
                },
              ]}
            />
            <Input
              placeholder={
                inviteMethod === 'email'
                  ? 'Enter student or parent email'
                  : 'Enter phone number (e.g., +91...)'
              }
              value={inviteValue}
              onChange={(e) => setInviteValue(e.target.value)}
              prefix={inviteMethod === 'email' ? <MailOutlined /> : <PhoneOutlined />}
              size="large"
              status={alreadyConnected ? 'error' : undefined}
            />
            {alreadyConnected && (
              <Alert
                type="warning"
                showIcon
                message={
                  alreadyConnected.status === 'INVITED'
                    ? `${alreadyConnected.student?.name ?? 'This student'} already has a pending invitation`
                    : `${alreadyConnected.student?.name ?? 'This student'} is already connected to your class`
                }
              />
            )}
          </Space>
        )}
      </Modal>
    </div>
  );
}
